import { useMutation, useQueryClient } from "@tanstack/react-query";

import type {
  AddAttachmentInputPayload,
  AddEventPayloadSchema,
  AddEventWithLegalHistorySchema,
  AddPassengersPayloadSchema,
  CreateClaimNotePayload,
  ExtractPassengersPayloadSchema,
  PatchCustomerNoteRequest,
  RemovePassengersPayloadSchema,
  SoftDeletePassengersPayloadSchema,
  UpdateClaimNotePayload,
  UpdatePassengerInput,
} from "@upclaim/sdk/src/data-contracts";

import { track } from "@/lib/analytics";
import { notify } from "@/lib/notify";
import { queryKeys } from "@/lib/query-keys";
import adminAPI from "@/services/api";

import type { ClaimWithRelations } from "./use-claim";

/**
 * Batch machine event — same payload for every claim, capped-concurrency calls
 * under a single progress toast (notify.batchPromise). Partial failures resolve;
 * everything is refetched afterwards either way (Algolia lags a few seconds).
 */
export function useBatchSendClaimEvent() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      claimIds,
      machineEvent,
      eventDate,
      internalNote,
    }: {
      claimIds: string[];
      machineEvent: string;
      eventDate?: Date;
      internalNote?: string;
    }) =>
      notify.batchPromise(
        claimIds,
        (claimId) => adminAPI.addEvent(claimId, { machineEvent, eventDate, internalNote }),
        {
          loading: "Envoi de l'événement",
          success: `Événement envoyé sur ${claimIds.length} dossier${claimIds.length > 1 ? "s" : ""}`,
          error: "Envoi incomplet",
        },
      ),
    onSuccess: async (_failedClaimIds, { claimIds, machineEvent }) => {
      for (const claimId of claimIds) {
        track("Claim State Changed", { claimId, machineEvent });
      }
      await queryClient.invalidateQueries({ queryKey: queryKeys.claims.all });
    },
  });
}

/** Mirrors ClaimStore.sendEvent — refetches the claim on success. */
export function useSendClaimEvent(claimId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (event: AddEventPayloadSchema | AddEventWithLegalHistorySchema) =>
      notify.promise(adminAPI.addEvent(claimId, event), { loading: "Envoi de l'événement…" }),
    onSuccess: async ({ success }, event) => {
      if (success) {
        track("Claim State Changed", { claimId, machineEvent: event.machineEvent });
        await queryClient.invalidateQueries({ queryKey: queryKeys.claims.detail(claimId) });
      }
    },
  });
}

/** Mirrors ClaimStore.extractPassengers — removes the users from the cached claim. */
export function useExtractPassengers(claimId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: ExtractPassengersPayloadSchema) =>
      notify.promise(adminAPI.extractPassengers(claimId, payload), {
        success: "Opération réalisée avec succès",
      }),
    onSuccess: ({ success }, { usersIds }) => {
      if (success) {
        queryClient.setQueryData<ClaimWithRelations>(queryKeys.claims.detail(claimId), (claim) => {
          if (!claim) {
            return claim;
          }
          return {
            ...claim,
            usersClaims: claim.usersClaims.filter((userClaim) => {
              return !usersIds.includes(userClaim.userId);
            }),
          };
        });
      }
    },
  });
}

/** Mirrors ClaimStore.removePassengers — flags the users as aborted in the cached claim. */
export function useRemovePassengers(claimId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: RemovePassengersPayloadSchema) =>
      notify.promise(adminAPI.removePassengers(claimId, payload)),
    onSuccess: ({ success }, { usersIds }) => {
      if (success) {
        queryClient.setQueryData<ClaimWithRelations>(queryKeys.claims.detail(claimId), (claim) => {
          if (!claim) {
            return claim;
          }
          return {
            ...claim,
            usersClaims: claim.usersClaims.map((userClaim) => {
              // @TODO abortionId returned from API
              if (usersIds.includes(userClaim.userId)) {
                return { ...userClaim, abortionId: 1 };
              }
              return userClaim;
            }),
          };
        });
      }
    },
  });
}

/** Mirrors ClaimStore.addAttachment. */
export function useAddClaimAttachment(claimId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (attachment: AddAttachmentInputPayload) =>
      notify.promise(adminAPI.addClaimAttachment(claimId, attachment), {
        loading: "Ajout du document…",
        success: "Document ajouté",
      }),
    onSuccess: (data) => {
      if (data) {
        queryClient.setQueryData<ClaimWithRelations>(queryKeys.claims.detail(claimId), (claim) => {
          if (!claim) {
            return claim;
          }
          return { ...claim, attachments: [...claim.attachments, data] };
        });
      }
    },
  });
}

/** Mirrors ClaimStore.removeAttachment. */
export function useRemoveClaimAttachment(claimId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (attachmentId: string) =>
      notify.promise(adminAPI.removeClaimAttachment(attachmentId), {
        loading: "Suppression du document…",
        success: "Document supprimé",
      }),
    onSuccess: ({ success }, attachmentId) => {
      if (success) {
        queryClient.setQueryData<ClaimWithRelations>(queryKeys.claims.detail(claimId), (claim) => {
          if (!claim) {
            return claim;
          }
          return {
            ...claim,
            attachments: claim.attachments.filter((attachment) => attachment.id !== attachmentId),
          };
        });
      }
    },
  });
}

/** Mirrors ClaimDocuments.addClaimDocument (Vue) — refetches the claim on success. */
export function useAddClaimDocument(claimId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: Parameters<typeof adminAPI.addClaimDocument>[1]) =>
      notify.promise(adminAPI.addClaimDocument(claimId, payload), {
        loading: "Document en cours d'envoi…",
        success: "Document ajouté avec succès",
      }),
    onSuccess: async ({ success }) => {
      if (success) {
        await queryClient.invalidateQueries({ queryKey: queryKeys.claims.detail(claimId) });
      }
    },
  });
}

/** Sandboxes and staging: the claim's real documents, watermarked SANDBOX (claim puller), then refetches the claim. */
export function usePullClaimRealDocuments(claimId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () =>
      notify.promise(adminAPI.pullClaimRealDocuments(claimId), {
        loading: "Téléchargement des pièces réelles…",
        success: ({ applied, boxFiles, durationMs }) =>
          `${applied} pièce(s) réelle(s) téléchargée(s), ${boxFiles} dans Box, en ${(durationMs / 1000).toFixed(1)} s`,
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.claims.detail(claimId) });
    },
  });
}

/** Mirrors ClaimDocuments.removeClaimDocument (Vue) — refetches the claim on success. */
export function useDeleteClaimDocument(claimId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (claimDocumentId: string) =>
      notify.promise(adminAPI.deleteClaimDocument(claimDocumentId), {
        loading: "Suppression du document…",
        success: "Document supprimé avec succès",
      }),
    onSuccess: async ({ success }) => {
      if (success) {
        await queryClient.invalidateQueries({ queryKey: queryKeys.claims.detail(claimId) });
      }
    },
  });
}

/**
 * A claim's file becomes a document of another type (or of a first one, in the file explorer) without being
 * deleted and added again (TECH-1391). Refetches the claim's documents and the explorer's « Type » column.
 */
export function useSetClaimFileDocumentType(claimId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ fileId, claimDocumentType }: { fileId: string; claimDocumentType: string }) =>
      notify.promise(adminAPI.setClaimFileDocumentType(claimId, fileId, { claimDocumentType }), {
        loading: "Modification du type…",
        success: "Type du document modifié",
      }),
    onSuccess: () =>
      Promise.all([
        queryClient.invalidateQueries({ queryKey: queryKeys.claims.detail(claimId) }),
        queryClient.invalidateQueries({ queryKey: queryKeys.fileExplorer.folders }),
      ]),
  });
}

/** Mirrors ProcedureDocuments.addClaimDocument (Vue) — refetches the claim on success. */
export function useAddProcedureDocument(claimId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: Parameters<typeof adminAPI.addProcedureDocument>[1]) =>
      notify.promise(adminAPI.addProcedureDocument(claimId, payload), {
        loading: "Document en cours d'envoi…",
        success: "Document ajouté avec succès",
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.claims.detail(claimId) });
    },
  });
}

/** Mirrors ProcedureDocuments.removeClaimDocument (Vue) — refetches the claim on success. */
export function useDeleteProcedureDocument(claimId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (procedureDocumentId: string) =>
      notify.promise(adminAPI.deleteProcedureDocument(procedureDocumentId), {
        loading: "Suppression du document…",
        success: () => ({
          message: "Document supprimé avec succès",
          description: "S'il s'agit d'un document Tribunal Digital, il est récupérable sur la plateforme.",
        }),
      }),
    onSuccess: async ({ success }) => {
      if (success) {
        await queryClient.invalidateQueries({ queryKey: queryKeys.claims.detail(claimId) });
      }
    },
  });
}

/** Mirrors the addPassengers flow of PassengerTable.vue — refetches the claim on success. */
export function useAddPassengers(claimId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: AddPassengersPayloadSchema) =>
      notify.promise(adminAPI.addPassengers(claimId, payload), {
        success: "Passager ajouté avec succès",
      }),
    onSuccess: async ({ success }) => {
      if (success) {
        await queryClient.invalidateQueries({ queryKey: queryKeys.claims.detail(claimId) });
      }
    },
  });
}

/** Mirrors the softDeletePassengers flow of PassengerTable.vue — refetches the claim on success. */
export function useSoftDeletePassengers(claimId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: SoftDeletePassengersPayloadSchema) =>
      notify.promise(adminAPI.softDeletePassengers(claimId, payload)),
    onSuccess: async ({ success }) => {
      if (success) {
        await queryClient.invalidateQueries({ queryKey: queryKeys.claims.detail(claimId) });
      }
    },
  });
}

/** Mirrors the updatePassenger flow of PassengerTable.vue — refetches the claim on success. */
function getUpdatePassengerSuccess({ email, birthdate }: UpdatePassengerInput): string {
  if (email === null) {
    return "Adresse e-mail retirée";
  }
  if (birthdate === null) {
    return "Date de naissance retirée";
  }
  if (birthdate !== undefined) {
    return "Date de naissance enregistrée";
  }
  return "Passager modifié avec succès";
}

export function useUpdatePassenger(claimId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    /**
     * `email: null` removes the address on file: the signer is asked for it again (TECH-1380).
     * `birthdate: null` removes a wrong birthdate: the passenger then counts as an adult (TECH-1393).
     */
    mutationFn: ({ userSegmentId, ...payload }: { userSegmentId: number } & UpdatePassengerInput) =>
      notify.promise(adminAPI.updatePassenger(claimId, userSegmentId, payload), {
        success: getUpdatePassengerSuccess(payload),
      }),
    onSuccess: async ({ success }) => {
      if (success) {
        await queryClient.invalidateQueries({ queryKey: queryKeys.claims.detail(claimId) });
      }
    },
  });
}

/** Mirrors ClaimNoteEditor.submitNote create path (Vue) — refetches the claim on success. */
export function useCreateClaimNote(claimId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: CreateClaimNotePayload) =>
      notify.promise(adminAPI.createClaimNote(claimId, payload), {
        loading: "Ajout de la note…",
        success: "Note ajoutée avec succès",
      }),
    onSuccess: async ({ success }) => {
      if (success) {
        await queryClient.invalidateQueries({ queryKey: queryKeys.claims.detail(claimId) });
      }
    },
  });
}

/** Mirrors ClaimNoteEditor edit path + ClaimNoteElement visibility toggle (Vue). */
export function useUpdateClaimNote(claimId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      noteId,
      payload,
      successMessage,
    }: {
      noteId: string;
      payload: UpdateClaimNotePayload;
      successMessage?: string;
    }) =>
      notify.promise(adminAPI.updateClaimNote(claimId, noteId, payload), {
        loading: "Modification de la note…",
        success: successMessage ?? "Note modifiée avec succès",
      }),
    onSuccess: async ({ success }) => {
      if (success) {
        await queryClient.invalidateQueries({ queryKey: queryKeys.claims.detail(claimId) });
      }
    },
  });
}

/** Mirrors ClaimNoteElement.deleteNote (Vue) — refetches the claim on success. */
export function useDeleteClaimNote(claimId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (noteId: string) =>
      notify.promise(adminAPI.deleteClaimNote(claimId, noteId), {
        loading: "Suppression de la note…",
        success: "Note supprimée",
      }),
    onSuccess: async ({ success }) => {
      if (success) {
        await queryClient.invalidateQueries({ queryKey: queryKeys.claims.detail(claimId) });
      }
    },
  });
}

/** Mirrors ClaimTimelineElement.deleteLastClaimEvent (Vue) — Upclaim only. */
export function useRemoveLastClaimEvent(claimId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () =>
      notify.promise(adminAPI.removeLastClaimEvent(claimId), {
        loading: "Suppression de l'évènement…",
        success: "Dernier évènement supprimé",
      }),
    onSuccess: async ({ success }) => {
      if (success) {
        await queryClient.invalidateQueries({ queryKey: queryKeys.claims.detail(claimId) });
      }
    },
  });
}

/**
 * Mirrors ClaimDetails.saveCustomerNote (Vue). Every field is optional: one left out is left as it
 * is (the customer note and the passengers' questions are saved from separate places).
 */
export function useSaveCustomerNote(claimId: string, success = "Note enregistrée avec succès") {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: PatchCustomerNoteRequest) =>
      notify.promise(adminAPI.saveCustomerNote(claimId, payload), { success }),
    onSuccess: async ({ success }) => {
      if (success) {
        await queryClient.invalidateQueries({ queryKey: queryKeys.claims.detail(claimId) });
      }
    },
  });
}

/** Mirrors ClaimDetails.saveAdminNote (Vue) — PATCH /admin/claims/{id}/note (EditorJS payload). */
export function useSaveAdminNote(claimId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: object) =>
      notify.promise(adminAPI.saveAdminNote(claimId, data), {
        success: "Note enregistrée avec succès",
      }),
    onSuccess: async ({ success }) => {
      if (success) {
        await queryClient.invalidateQueries({ queryKey: queryKeys.claims.detail(claimId) });
      }
    },
  });
}
